import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import DatePickerIT from "../components/DatePickerIT";
import { Mail, Lock, User, Eye, EyeOff, Loader2, Check } from "lucide-react";
import { BACKEND_URL } from "../lib/config";

export default function Login() {
  const [tab, setTab] = useState("login");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signupConfig, setSignupConfig] = useState(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    gender: "",
    birthDate: "",
  });
  const [consents, setConsents] = useState({
    terms: false,
    marketing: false,
    analytics: false,
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/config/signup`)
      .then((r) => setSignupConfig(r.data))
      .catch(() => setSignupConfig(null));
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (tab === "signup" && !consents.terms) {
      setError("Per registrarti devi accettare i Termini di Servizio e la Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      if (tab === "login") {
        await login({ email: form.email, password: form.password });
      } else {
        await register({
          ...form,
          _config: signupConfig,
          consent_terms: consents.terms,
          consent_marketing: consents.marketing,
          consent_analytics: consents.analytics,
        });
      }
      navigate(from === "/login" ? "/" : from, { replace: true });
    } catch (err) {
      setError(err.message || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex justify-center">
      {/* backdrop */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(700px 400px at 50% -10%, rgba(20,194,161,0.35) 0%, rgba(20,194,161,0) 60%), #141818",
        }}
      />

      <div className="w-full max-w-[430px] min-h-screen bg-[#2b3134] flex flex-col">
        <div className="pt-10 pb-6 flex flex-col items-center gap-3">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{
              background:
                "linear-gradient(155deg, #12b096 0%, #0a8670 55%, #083d37 100%)",
              boxShadow: "0 12px 28px -12px rgba(20,194,161,0.55)",
            }}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <Logo />
          <div className="text-white/60 text-[12px] uppercase tracking-[0.25em]">
            SmartDream
          </div>
        </div>

        <div className="px-6 flex-1">
          <div className="relative grid grid-cols-2 p-1 bg-white/5 rounded-2xl border border-white/10">
            <button
              onClick={() => setTab("login")}
              className={`relative z-10 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                tab === "login" ? "text-white" : "text-white/60"
              }`}
            >
              Accedi
            </button>
            <button
              onClick={() => setTab("signup")}
              className={`relative z-10 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                tab === "signup" ? "text-white" : "text-white/60"
              }`}
            >
              Crea profilo
            </button>
            <div
              className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-xl transition-all"
              style={{
                left: tab === "login" ? 4 : "calc(50% + 0px)",
                background:
                  "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                boxShadow: "0 6px 18px -8px rgba(20,194,161,0.55)",
              }}
            />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3">
            {tab === "signup" && (
              <>
                <InputRow
                  icon={<User size={16} />}
                  placeholder="Nome"
                  value={form.firstName}
                  onChange={onChange("firstName")}
                  data-testid="signup-firstname"
                />
                <InputRow
                  icon={<User size={16} />}
                  placeholder="Cognome"
                  value={form.lastName}
                  onChange={onChange("lastName")}
                  data-testid="signup-lastname"
                />

                {/* Sesso (opzioni dal backend) */}
                <div>
                  <div className="text-white/60 text-[11px] uppercase tracking-wider mb-2 px-1">
                    Sesso
                  </div>
                  <div
                    className="grid gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${
                        (signupConfig?.genders || [
                          { value: "MASCHIO", label: "Maschio" },
                          { value: "FEMMINA", label: "Femmina" },
                          { value: "ALTRO", label: "Altro" },
                        ]).length
                      }, minmax(0, 1fr))`,
                    }}
                  >
                    {(signupConfig?.genders || [
                      { value: "MASCHIO", label: "Maschio" },
                      { value: "FEMMINA", label: "Femmina" },
                      { value: "ALTRO", label: "Altro" },
                    ]).map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        data-testid={`signup-gender-${o.value.toLowerCase()}`}
                        onClick={() => setForm({ ...form, gender: o.value })}
                        className={`h-11 rounded-xl text-sm font-medium border transition-all ${
                          form.gender === o.value
                            ? "text-white border-[#14c2a1]"
                            : "text-white/70 border-white/10 bg-white/5 hover:border-white/20"
                        }`}
                        style={
                          form.gender === o.value
                            ? {
                                background:
                                  "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                                boxShadow:
                                  "0 8px 20px -10px rgba(20,194,161,0.5)",
                              }
                            : {}
                        }
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Data di nascita */}
                <div>
                  <div className="text-white/60 text-[11px] uppercase tracking-wider mb-2 px-1">
                    Data di nascita
                  </div>
                  <DatePickerIT
                    value={form.birthDate}
                    onChange={(iso) => setForm({ ...form, birthDate: iso })}
                    maxDate={new Date()}
                    testid="signup-birthdate"
                  />
                </div>
              </>
            )}
            <InputRow
              icon={<Mail size={16} />}
              placeholder="Email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              data-testid="auth-email"
            />
            <InputRow
              icon={<Lock size={16} />}
              placeholder="Password"
              type={showPass ? "text" : "password"}
              value={form.password}
              onChange={onChange("password")}
              data-testid="auth-password"
              right={
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="text-white/60 hover:text-white"
                  aria-label="toggle password"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />

            {tab === "login" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  data-testid="forgot-password-link"
                  className="text-white/60 hover:text-white text-xs"
                >
                  Password dimenticata?
                </button>
              </div>
            )}

            {tab === "signup" && (
              <div className="space-y-2 pt-1" data-testid="signup-consents">
                <ConsentCheckbox
                  required
                  testid="consent-terms"
                  checked={consents.terms}
                  onChange={(v) => setConsents({ ...consents, terms: v })}
                  label={
                    <>
                      Accetto i{" "}
                      <a
                        href="https://smartmattressalex.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#67e8d1] underline underline-offset-2"
                      >
                        Termini di Servizio
                      </a>{" "}
                      e la{" "}
                      <a
                        href="https://smartmattressalex.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#67e8d1] underline underline-offset-2"
                      >
                        Privacy Policy
                      </a>
                    </>
                  }
                />
                <ConsentCheckbox
                  testid="consent-marketing"
                  checked={consents.marketing}
                  onChange={(v) => setConsents({ ...consents, marketing: v })}
                  label="Acconsento al trattamento dati per finalità di marketing (opzionale)"
                />
                <ConsentCheckbox
                  testid="consent-analytics"
                  checked={consents.analytics}
                  onChange={(v) => setConsents({ ...consents, analytics: v })}
                  label="Acconsento all'analisi anonima dei dati per ricerca (opzionale)"
                />
              </div>
            )}

            {error && (
              <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full h-12 rounded-2xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
              style={{
                background:
                  "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
                boxShadow: "0 14px 30px -12px rgba(20,194,161,0.55)",
              }}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {tab === "login" ? "Accedi" : "Crea profilo"}
            </button>
          </form>

          <p className="mt-8 mb-10 text-center text-white/50 text-[11px] leading-relaxed">
            Continuando accetti i Termini di Servizio
            <br />e la Privacy Policy di Materassi &amp; Materassi.
          </p>
        </div>
      </div>
    </div>
  );
}

function InputRow({ icon, right, ...props }) {
  return (
    <div className="flex items-center h-12 px-4 gap-3 rounded-2xl bg-white/5 border border-white/10 focus-within:border-[#14c2a1] focus-within:ring-2 focus-within:ring-[#14c2a1]/25 transition-all">
      <span className="text-white/50">{icon}</span>
      <input
        {...props}
        className="flex-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none"
      />
      {right}
    </div>
  );
}

function ConsentCheckbox({ checked, onChange, label, required, testid }) {
  return (
    <label
      className="flex items-start gap-2.5 cursor-pointer select-none group"
      data-testid={testid}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 transition-all ${
          checked
            ? "bg-[#14c2a1] border-[#14c2a1]"
            : "border-white/30 bg-white/5 group-hover:border-white/50"
        }`}
      >
        {checked && <Check size={12} strokeWidth={3} className="text-[#0a3a34]" />}
      </button>
      <span className="text-white/70 text-[12px] leading-snug">
        {label}
        {required && <span className="text-rose-400 ml-1">*</span>}
      </span>
    </label>
  );
}
